import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface CoachOrganizationContext {
  organizationId: string;
  organizationName: string;
  memberRole: string;
}

type OrganizationMemberRow = {
  organization_id: string;
  role: string;
};

type OrganizationRow = {
  id: string;
  name: string;
};

export function useCoachOrganization() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['coach-organization', user?.id],
    queryFn: async (): Promise<CoachOrganizationContext | null> => {
      if (!user?.id) return null;

      const { data: membership, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id, role')
        .eq('profile_id', user.id)
        .order('joined_at', { ascending: true })
        .limit(1)
        .maybeSingle<OrganizationMemberRow>();

      if (memberError) throw memberError;
      if (!membership?.organization_id) return null;

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('id', membership.organization_id)
        .maybeSingle<OrganizationRow>();

      if (orgError) throw orgError;
      if (!org) return null;

      return {
        organizationId: org.id,
        organizationName: org.name,
        memberRole: membership.role,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}
